import React from 'react';
import { Link } from 'react-router-dom';
import { TrafficLight } from './TrafficLight';
import { ArrowRight, AlertTriangle } from 'lucide-react';

const CompanyCard = ({ company, status }) => {
  const progress = status?.total_krs > 0 
    ? Math.round((status?.on_track_krs / status?.total_krs) * 100) 
    : 0;

  // Emoji icons for companies
  const companyEmojis = {
    UIS: '⚖️',
    UAL: '🤖',
    USL: '📚',
    Medio: '📰',
    CEO: '👔',
  };

  return (
    /* Fitts's Law: Large clickable card area */
    <Link 
      to={`/scorecard/${company.company_id}`}
      className="company-card block p-5 group"
      data-testid={`company-card-${company.code}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="text-3xl">{companyEmojis[company.code] || '🏢'}</span>
          <div>
            <h3 className="font-semibold text-gray-900 text-base">
              {company.name}
            </h3>
            <p className="text-xs text-gray-500">{company.code}</p>
          </div>
        </div>
        <TrafficLight status={status?.status_color || 'gray'} size="lg" />
      </div>

      {/* Progress - Miller's Law: Simple visual indicator */}
      <div className="mb-4">
        <div className="h-2 rounded-full bg-gray-100 overflow-hidden mb-2">
          <div 
            className="h-full bg-blue-500 transition-all duration-500 rounded-full"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-500">
            {status?.on_track_krs || 0}/{status?.total_krs || 0} KRs
          </span>
          <span className="font-semibold text-gray-700">{progress}%</span>
        </div>
      </div>

      {/* Blocked KR Warning - Hick's Law: Only show if critical */}
      {status?.blocked_kr && (
        <div className="p-2.5 bg-red-50 border border-red-100 rounded-lg flex items-center gap-2 mb-4">
          <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <span className="text-xs text-red-700 truncate font-medium">
            {status.blocked_kr.title}
          </span>
        </div>
      )}

      {/* Critical Tasks Preview - Miller's Law: Max 2 items */}
      {status?.critical_tasks?.length > 0 && (
        <div className="mb-4">
          <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Críticas</span>
          <div className="mt-1.5 space-y-1">
            {status.critical_tasks.slice(0, 2).map((task, idx) => (
              <div key={idx} className="text-xs text-gray-600 truncate flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-amber-500 rounded-full flex-shrink-0" />
                {task.title}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer - Fitts's Law: Clear action indicator */}
      <div className="flex items-center justify-end pt-3 border-t border-gray-100">
        <span className="text-sm text-gray-500 flex items-center gap-1.5 group-hover:text-gray-900 group-hover:gap-2 transition-all font-medium">
          Ver Scorecard
          <ArrowRight className="w-4 h-4" />
        </span>
      </div>
    </Link>
  );
};

export default CompanyCard;
